import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  InputAdornment,
  TextField,
  Modal,
  Box,
  Typography,
  Divider,
  Fade,
  IconButton as MuiIconButton,
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Launch as LaunchIcon, Close as CloseIcon } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useTheme as useAppTheme } from '../../context/ThemeContext';
import { useApi } from 'context/ApiProvider';
import { useSelectedDesignStore } from 'context/SelectedDesignContext';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { fetchAdminTickets } from 'services/adminService';
import { PaginatedSearchRequestDto } from 'models/PaginatedSearchRequestDto';
import { formatLocalDate } from 'Utils/StringUtils';

const TicketsTable: React.FC = () => {
  const { isDarkMode } = useAppTheme();
  const api = useApi();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [ticketData, setTicketData] = useState<AdminTicketData[]>([]);
  const [selected, setSelected] = useState<number | null>(null);
  const [selectedTicket, setSelectedTicket] = useState<AdminTicketData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(true);
  const pageSize = 10;
  const totalPages = useRef(1);
  
  useEffect(() => {
    const fetchTickets = async () => {
      if (!api) return;
      try {
        setLoading(true);
        const ticketRequestDto = new PaginatedSearchRequestDto({
          pageNumber,
          pageSize,
          searchTerm,
        });
        const res = await fetchAdminTickets(api, ticketRequestDto);
        if (!res) {
          setError('No response from server!');
          setTicketData([]);
          setHasNextPage(false);
          return;
        }
        if (!res?.data || res.data.length < 1) {
          setError('No tickets found for this page.');
          setTicketData([]);
          setHasNextPage(false);
          return;
        }
        totalPages.current = res.totalPages;
        setPageNumber(res.pageNumber);
        setHasNextPage(res.pageNumber < res.totalPages);
        const mappedData: AdminTicketData[] = Array.isArray(res?.data) && res.data.length > 0
          ? res.data
          : [];
        setTicketData(mappedData);
        setError(mappedData.length < 1 ? 'No tickets found for this page' : null);
      } catch (err: any) {
        setError('Failed to fetch tickets');
        console.error(err);
        toast.error(err.response?.data?.error?.message || 'Failed to fetch tickets');
        setTicketData([]);
        setHasNextPage(false);
      } finally {
        setLoading(false);
      }
    };
    fetchTickets();
  }, [api, pageNumber, searchTerm]);

  const filteredData = ticketData.filter(
    (row) =>
      row.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      row.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      row.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      row.designName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      row.createdAt.includes(searchTerm)
  );

  const handleRowClick = (id: number) => {
    setSelected(id === selected ? null : id);
  };

  const isSelected = (id: number) => id === selected;

  const handleOpenDetail = (ticket: AdminTicketData) => {
    setSelectedTicket(ticket);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTicket(null);
  };

  const handleOpenDesign = (designId: number) => {
    useSelectedDesignStore.getState().setDesignId(designId);
    navigate('/');
  };

  const handleNextPage = () => {
    setPageNumber((prev) => prev + 1);
  };

  const handlePreviousPage = () => {
    if (pageNumber > 1) {
      setPageNumber((prev) => prev - 1);
    }
  };
  
  const handleSelectPage = async (pageNumber: any) => {
    setPageNumber(pageNumber);
  }

  return (
    <div style={{ padding: 0, backgroundColor: isDarkMode ? '#1e1e1e' : '#f5f5f5' }}>

      <div className="admin-toolbar">
        <TextField
          className="admin-search"
          placeholder="Search tickets..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: isDarkMode ? '#e0e0e0' : '#666666', opacity: 0.8 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            flex: '1 1 200px',
            minWidth: 0,
            '& .MuiInputBase-root': {
              borderRadius: '4px',
              background: isDarkMode ? '#444444' : '#ffffff',
              color: isDarkMode ? '#ffffff' : '#076596',
              fontSize: '0.9rem',
              fontWeight: 500,
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
              padding: '2px 10px',
            },
            '& .MuiInputBase-input': { padding: '8px 8px 8px 0' },
          }}
        />
        <Button
          variant="outlined"
          startIcon={<FilterListIcon />}
          sx={{
            borderColor: '#076596',
            color: isDarkMode ? '#ffffff' : '#076596',
            backgroundColor: isDarkMode ? '#444444' : '#ffffff',
            borderWidth: '1px',
            fontWeight: 500,
            flex: { xs: '1 1 auto', sm: '0 0 auto' },
            '&:hover': { borderColor: '#0a6fb4', color: '#0a6fb4' },
          }}
        >
          Filter
        </Button>
      </div>
      <TableContainer
        className="admin-table-scroll"
        component={Paper}
        sx={{
          backgroundColor: isDarkMode ? '#444444' : '#ffffff',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          borderRadius: '8px',
          border: isDarkMode ? '1px solid #076596' : '1px solid rgba(255, 255, 255, 0.3)',
          maxHeight: { xs: '60vh', md: '70vh' },
          overflow: 'auto',
        }}
      >
        <Table sx={{ minWidth: 650 }} aria-label="tickets table" stickyHeader>
          <TableHead>
            <TableRow
              sx={{
                '& th': {
                  backgroundColor: 'var(--accent-color)',
                  color: '#ffffff',
                  fontWeight: 700,
                  padding: '12px 16px',
                  fontSize: '0.95rem',
                  letterSpacing: '0.01em',
                  borderBottom: '1px solid var(--toolbar-border)',
                },
              }}
            >
              <TableCell>ID</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography
                    sx={{
                      color: isDarkMode ? '#ffffff' : '#076596',
                      fontSize: '1rem',
                      fontWeight: 500,
                      padding: '16px',
                    }}
                  >
                    Loading...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : error || filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography
                    sx={{
                      color: isDarkMode ? '#ff6b6b' : '#e74c3c',
                      fontSize: '1rem',
                      fontWeight: 500,
                      padding: '16px',
                    }}
                  >
                    {error || 'No matching tickets found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredData.map((row) => (
                <TableRow
                  key={row.ticketId}
                  hover
                  onClick={() => handleRowClick(row.ticketId)}
                  onDoubleClick={() => handleOpenDetail(row)}
                  selected={isSelected(row.ticketId)}
                  sx={{
                    backgroundColor: isSelected(row.ticketId)
                      ? 'var(--table-row-bg-focus)'
                      : undefined,
                    '& .MuiTableCell-root': { backgroundColor: 'transparent !important' },
                    '&.MuiTableRow-hover:hover.MuiTableRow-hover:hover': {
                      backgroundColor: 'var(--table-row-bg-hover)',
                    },
                    '& td, & th': {
                      color: 'var(--text-color)',
                      padding: '10px 16px',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
                    },
                    '&.Mui-selected': {
                      backgroundColor: 'var(--table-row-bg-focus)',
                    },
                    cursor: 'pointer',
                  }}
                >
                  <TableCell component="th" scope="row">{row.ticketId}</TableCell>
                  <TableCell>{row.title}</TableCell>
                  <TableCell>{row.userName}</TableCell>
                  <TableCell>{formatLocalDate(row.createdAt)}</TableCell>
                  <TableCell align="center">
                    {/* <IconButton
                      size="small"
                      aria-label="view details"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDetail(row);
                      }}
                      sx={{ color: isDarkMode ? '#ffffff' : '#076596', '&:hover': { color: '#0a6fb4' } }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton> */}
                    <IconButton
                      size="small"
                      aria-label="open design"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenDesign(row.designId);
                      }}
                      sx={{ color: isDarkMode ? '#ffffff' : '#076596', '&:hover': { color: '#0a6fb4' } }}
                    >
                      <LaunchIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <div className="admin-pagination" style={{ justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            disabled={pageNumber === 1}
            onClick={handlePreviousPage}
            sx={{
              borderColor: '#076596',
              color: isDarkMode ? '#ffffff' : '#076596',
              backgroundColor: isDarkMode ? '#444444' : '#ffffff',
              padding: '8px 16px',
              fontWeight: 500,
              fontSize: '0.9rem',
              '&:hover': { borderColor: '#0a6fb4', color: '#0a6fb4' },
              '&.Mui-disabled': {
                borderColor: isDarkMode ? '#555555' : '#cccccc',
                color: isDarkMode ? '#777777' : '#999999',
              },
            }}
          >
            Prev
          </Button>
          {(() => {
            const total = totalPages.current;
            const pagesToShow: (number | 'ellipsis')[] = [];

            if (total <= 5) {
              for (let i = 1; i <= total; i++) pagesToShow.push(i);
            } else {
              if (pageNumber === 1) pagesToShow.push(1, 2, 'ellipsis', total - 1, total);
              else if (pageNumber < total - 1) pagesToShow.push(pageNumber - 1, pageNumber, 'ellipsis', total - 1, total);
              else if (pageNumber === total - 1) pagesToShow.push(1, 2, 'ellipsis', pageNumber, total)
              else pagesToShow.push(1, 2, 'ellipsis', pageNumber - 1, pageNumber)
            }

            return pagesToShow.map((p, idx) => {
              if (p === 'ellipsis') {
              return (<div key={`ellipsis-${idx}`} style={{display: 'flex', flexDirection: 'column', justifyContent: 'end'}}> ... </div>);
              }

              const pageIndex = p as number;
              return (
              <Button
                key={`page-${pageIndex}`}
                disabled={pageIndex === pageNumber}
                variant="outlined"
                onClick={() => handleSelectPage(pageIndex)}
                sx={{
                borderColor: '#076596',
                color: isDarkMode ? '#ffffff' : '#076596',
                backgroundColor: isDarkMode ? '#444444' : '#ffffff',
                padding: '8px 16px',
                fontWeight: 500,
                fontSize: '0.9rem',
                '&:hover': { borderColor: '#0a6fb4', color: '#0a6fb4' },
                '&.Mui-disabled': {
                  borderColor: isDarkMode ? '#555555' : '#cccccc',
                  color: isDarkMode ? '#777777' : '#999999',
                },
                }}
              >
                {pageIndex}
              </Button>
              );
            });
          })()}
          <Button
            variant="outlined"
            disabled={!hasNextPage}
            onClick={handleNextPage}
            sx={{
              borderColor: '#076596',
              color: isDarkMode ? '#ffffff' : '#076596',
              backgroundColor: isDarkMode ? '#444444' : '#ffffff',
              padding: '8px 16px',
              fontWeight: 500,
              fontSize: '0.9rem',
              '&:hover': { borderColor: '#0a6fb4', color: '#0a6fb4' },
              '&.Mui-disabled': {
                borderColor: isDarkMode ? '#555555' : '#cccccc',
                color: isDarkMode ? '#777777' : '#999999',
              },
            }}
          >
            Next
          </Button>
        </div>
        {/* <Button
          variant="outlined"
          sx={{
            borderColor: '#076596',
            color: isDarkMode ? '#ffffff' : '#076596',
            backgroundColor: isDarkMode ? '#444444' : '#ffffff',
            padding: '8px 16px',
            fontWeight: 500,
            fontSize: '0.9rem',
            '&:hover': { borderColor: '#0a6fb4', color: '#0a6fb4' },
          }}
        >
          Export Data
        </Button> */}
      </div>

      {/* Enhanced Detail Modal */}
      {selectedTicket && (
        <Modal
          open={isModalOpen}
          onClose={handleCloseModal}
          aria-labelledby="ticket-detail-modal-title"
          closeAfterTransition
        >
          <Fade in={isModalOpen}>
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: { xs: '94%', sm: 600, md: 700 },
                bgcolor: isDarkMode ? '#2a2a2a' : '#ffffff',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                p: { xs: 2, sm: 3, md: 4 },
                borderRadius: '16px',
                border: isDarkMode ? '1px solid #076596' : '1px solid rgba(0, 0, 0, 0.1)',
                maxHeight: '85vh',
                overflowY: 'auto',
                '&:focus': { outline: 'none' },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3,
                  gap: 1,
                  flexWrap: 'wrap',
                  borderBottom: isDarkMode ? '1px solid #444' : '1px solid #e0e0e0',
                  pb: 2,
                }}
              >
                <Typography
                  id="ticket-detail-modal-title"
                  variant="h5"
                  component="h2"
                  sx={{
                    fontWeight: 700,
                    color: isDarkMode ? '#ffffff' : '#076596',
                    fontSize: { xs: '1.5rem', sm: '1.75rem' },
                  }}
                >
                  Ticket #{selectedTicket.ticketId}
                </Typography>
                <IconButton
                  onClick={handleCloseModal}
                  sx={{
                    color: isDarkMode ? '#e0e0e0' : '#076596',
                    '&:hover': { color: '#0a6fb4' },
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  backgroundColor: isDarkMode ? '#333333' : '#f9f9f9',
                  p: 3,
                  borderRadius: '12px',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                }}
              >
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: isDarkMode ? '#e0e0e0' : '#076596',
                      fontWeight: 600,
                      mb: 0.5,
                      fontSize: '0.9rem',
                    }}
                  >
                    Title
                  </Typography>
                  <Typography
                    sx={{
                      color: isDarkMode ? '#ffffff' : '#076596',
                      fontSize: '1.1rem',
                      fontWeight: 500,
                    }}
                  >
                    {selectedTicket.title}
                  </Typography>
                </Box>
                <Divider sx={{ bgcolor: isDarkMode ? '#444' : '#e0e0e0' }} />
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: isDarkMode ? '#e0e0e0' : '#076596',
                      fontWeight: 600,
                      mb: 0.5,
                      fontSize: '0.9rem',
                    }}
                  >
                    Description
                  </Typography>
                  <Typography
                    sx={{
                      color: isDarkMode ? '#ffffff' : '#076596',
                      fontSize: '1rem',
                      lineHeight: 1.6,
                    }}
                  >
                    {selectedTicket.description || 'No description provided'}
                  </Typography>
                </Box>
                <Divider sx={{ bgcolor: isDarkMode ? '#444' : '#e0e0e0' }} />
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                    gap: 2,
                  }}
                >
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: isDarkMode ? '#e0e0e0' : '#076596',
                        fontWeight: 600,
                        mb: 0.5,
                        fontSize: '0.9rem',
                      }}
                    >
                      User
                    </Typography>
                    <Typography
                      sx={{
                        color: isDarkMode ? '#ffffff' : '#076596',
                        fontSize: '1rem',
                      }}
                    >
                      {selectedTicket.userName}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: isDarkMode ? '#e0e0e0' : '#076596',
                        fontWeight: 600,
                        mb: 0.5,
                        fontSize: '0.9rem',
                      }}
                    >
                      Design
                    </Typography>
                    <Typography
                      sx={{
                        color: isDarkMode ? '#ffffff' : '#076596',
                        fontSize: '1rem',
                      }}
                    >
                      {selectedTicket.designName} (ID: {selectedTicket.designId})
                    </Typography>
                  </Box>
                </Box>
                <Divider sx={{ bgcolor: isDarkMode ? '#444' : '#e0e0e0' }} />
                <Box>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: isDarkMode ? '#e0e0e0' : '#076596',
                      fontWeight: 600,
                      mb: 0.5,
                      fontSize: '0.9rem',
                    }}
                  >
                    Created At
                  </Typography>
                  <Typography
                    sx={{
                      color: isDarkMode ? '#ffffff' : '#076596',
                      fontSize: '1rem',
                    }}
                  >
                    {new Date(selectedTicket.createdAt).toLocaleString()}
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  mt: 3,
                  gap: 2,
                  pt: 2,
                  flexDirection: { xs: 'column-reverse', sm: 'row' },
                  flexWrap: 'wrap',
                  borderTop: isDarkMode ? '1px solid #444' : '1px solid #e0e0e0',
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleCloseModal}
                  sx={{
                    borderColor: '#076596',
                    color: isDarkMode ? '#ffffff' : '#076596',
                    padding: '8px 20px',
                    fontWeight: 600,
                    borderRadius: '8px',
                    width: { xs: '100%', sm: 'auto' },
                    '&:hover': {
                      borderColor: '#0a6fb4',
                      backgroundColor: 'rgba(7, 101, 150, 0.1)',
                    },
                  }}
                >
                  Close
                </Button>
                <Button
                  variant="contained"
                  onClick={() => handleOpenDesign(selectedTicket.designId)}
                  sx={{
                    backgroundColor: 'var(--accent-color)',
                    color: '#ffffff',
                    padding: '8px 20px',
                    fontWeight: 600,
                    borderRadius: '8px',
                    width: { xs: '100%', sm: 'auto' },
                    '&:hover': {
                      backgroundColor: '#0a6fb4',
                    },
                  }}
                >
                  Open Design
                </Button>
              </Box>
            </Box>
          </Fade>
        </Modal>
      )}
    </div>
  );
};

export default TicketsTable;